import type { ChartAnalyticsDefinition } from '@/features/chart/lib/config/chart-analytics'
import {
  CHART_ANALYTICS_BY_KEY,
  CHART_ANALYTICS_DEFINITIONS,
} from '@/features/chart/lib/config/chart-analytics'
import { SPECIAL_ANALYTICS_STUDY_NAMES } from '@/features/chart/lib/config/chart-indicator-catalog'
import { CandleFamily } from '@/types/generated/public_api'

/**
 * Families the TV candle fetch must request for the given active study ids.
 * CORE is always included; ids without a family (TV built-ins like 'tv-sma',
 * or own-fetch metrics that carry `source` instead) are ignored.
 * Returned sorted ascending so array equality doubles as set equality.
 */
export function computeRequestedFamilies(activeMetrics: readonly string[]): CandleFamily[] {
  const families = new Set<CandleFamily>([CandleFamily.CANDLE_FAMILY_CORE])
  for (const metric of activeMetrics) {
    const definition = (CHART_ANALYTICS_BY_KEY as Record<string, ChartAnalyticsDefinition>)[metric]
    if (definition?.family !== undefined) families.add(definition.family)
    // The three merged volume studies can switch block trades off and derive
    // quote-side buy/sell values from order-flow fields at runtime.
    if (
      metric === 'volumeBaseBlockIncl' ||
      metric === 'buyVolumeBaseBlockIncl' ||
      metric === 'sellVolumeBaseBlockIncl'
    ) {
      families.add(CandleFamily.CANDLE_FAMILY_ORDER_FLOW)
      families.add(CandleFamily.CANDLE_FAMILY_BLOCK_INCL)
    }
  }
  return [...families].sort((a, b) => a - b)
}

/**
 * Recover the candle families needed by studies in a persisted TradingView
 * layout before the widget has restored those studies. Saved layouts contain
 * either the display name or the custom metainfo id, depending on TV version.
 * Starting with this mask avoids the legacy empty-array = ALL request while
 * still giving restored studies their data in the first candle response.
 */
export function computeInitialRequestedFamilies(savedData?: string): CandleFamily[] {
  if (!savedData) return computeRequestedFamilies([])

  const specialStudyHints: Partial<Record<string, string[]>> = {
    buySellLiq: [SPECIAL_ANALYTICS_STUDY_NAMES.buySellLiq, 'OrderXLiquidationSplit@'],
    netBuyTradeCount: [SPECIAL_ANALYTICS_STUDY_NAMES.netBuyTradeCount, 'OrderXTradeCountBuySell@'],
  }
  const metrics: string[] = []
  for (const definition of CHART_ANALYTICS_DEFINITIONS) {
    const specialMatch = specialStudyHints[definition.key]?.some((hint) =>
      hint.includes(' ') ? savedData.includes(JSON.stringify(hint)) : savedData.includes(hint)
    )
    if (
      savedData.includes(JSON.stringify(`OrderX ${definition.label}`)) ||
      savedData.includes(`OrderX${definition.key}@`) ||
      specialMatch
    ) {
      metrics.push(definition.key)
    }
  }

  return computeRequestedFamilies(metrics)
}

/**
 * True when `next` needs a family the current request set lacks. An empty
 * `current` means ALL families (the legacy default) — never widened.
 */
export function isFamilyWidening(
  current: readonly CandleFamily[],
  next: readonly CandleFamily[]
): boolean {
  if (current.length === 0) return false
  const have = new Set(current)
  return next.some((family) => !have.has(family))
}

export type FamilyMaskUpdate = { families: CandleFamily[]; widened: boolean }

/**
 * Decide the next family mask from the active studies. `widened === true`
 * means already-loaded candles miss columns the studies need — the caller
 * must refetch (widget.resetCache() + chart.resetData()).
 */
export function planFamilyMaskUpdate(
  current: readonly CandleFamily[],
  activeMetrics: readonly string[]
): FamilyMaskUpdate {
  const families = computeRequestedFamilies(activeMetrics)
  return { families, widened: isFamilyWidening(current, families) }
}
